/**
 * MSR Portal — Event detail.
 *
 * Mobile-first view of one event the current rep works. Four tabs (MSR plan decision 2 and round 9, D13–D15),
 * kept in `?tab=` so a reload or Back keeps the tab:
 *   - Overview: the event's numbers (attendance entry for class / seminar / speaking engagement, close rate).
 *   - Operations: only at check-in events (range nights, gun shows) and only for a rep who can operate the
 *     event — attendees, check-in rewards and walk-in. The server enforces every action.
 *   - Attendees: ONE list (MSR QA notes 2026-10-19, item 12: no Everyone / Mine toggle) -- the full roster for a
 *     rep who can operate the event, with the rep's own QR sign-ups tagged in it; otherwise just those sign-ups.
 *     "Verify member sign-ups" sits below it (verification is not a tab of its own).
 *   - Calls to Action, read-only (EVT-03): the event's QR code -- the one set up from the event account's CTA for
 *     this activity (mig 530) -- first, then the rep's own QR codes. Nothing here adds, edits or regenerates a CTA.
 * MSRs cannot create, duplicate or edit events here; nothing on this page offers it.
 *
 * Every RPC derives the actor from auth.uid(); no profile id is passed from the client.
 */

import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EventDailyHoursList,
  EventDailyHoursNotice,
} from "../../components/events/EventDailyHours";
import { useEventDayHours } from "../../hooks/useEventDayHours";
import { resolveDailyHours } from "../../utils/eventDayHours";
import { formatEventDateRange } from "../../utils/eventTime";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";
import EventOperationsTab from "../../components/events/tabs/EventOperationsTab";
import EventWalkinPanel from "../../components/events/EventWalkinPanel";
import {
  getMsrEventCtas,
  getMsrEventAttendees,
  getMsrSchedule,
  type MsrEventCtaRow,
  type MsrEventAttendeeRow,
} from "../../services/msrPortal.service";
import { getEventById } from "../../services/events.service";
import {
  getEventOperationsRoster,
  type OperationsRosterRow,
} from "../../services/eventOperations.service";
import { eventKeys } from "../../hooks/useEvents";
import { useToast } from "../../components/ui/Toast";
import EventCloseRateCard from "../../components/events/EventCloseRateCard";
import MemberVerificationTab from "../../components/events/tabs/MemberVerificationTab";
import {
  ActivityVerifySaleCard,
  ManualCountsCard,
} from "../../components/msr/MsrActivityCards";
import {
  getEventAttributionSummary,
  getEventSignupPool,
} from "../../services/eventAttribution.service";
import { verificationCounts } from "../../utils/msrVerificationSummary";
import { eventLocalDate, dayLabel } from "../../utils/msrEventDay";
import {
  MsrEventDayRunPanel,
  useEventDayRunState,
} from "../../components/events/EventDayRun";
import { ctaActivityLabel } from "../../utils/ctaActivityType";
import { eventTypeIsNoPromo } from "../../utils/eventTypePolicy";
import EventHostCTAsPanel from "../../components/events/EventHostCTAsPanel";
import {
  describeAccountCta,
  EVENT_QR_NOT_READY,
  eventQrNotReady,
  splitMsrEventCtas,
} from "../../utils/msrEventCtas";
import {
  formatEventLocation,
  formatEventSpan,
  formatTimeInZone,
} from "../../utils/msrEventFormat";

const CtaCard: React.FC<{ cta: MsrEventCtaRow; account?: boolean }> = ({
  cta,
  account = false,
}) => {
  const { showToast } = useToast();

  const handleCopyUrl = async () => {
    if (cta.generated_url === null || cta.generated_url === undefined) {
      return;
    }
    try {
      await navigator.clipboard.writeText(cta.generated_url);
      showToast("success", "URL copied");
    } catch {
      showToast("error", "Couldn't copy URL");
    }
  };

  const handleDownloadQr = async () => {
    if (cta.qr_code_url === null || cta.qr_code_url === undefined) {
      return;
    }
    try {
      const response = await fetch(cta.qr_code_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (cta.name ?? "qr-code").replace(/[^a-zA-Z0-9]+/g, "-");
      a.download = `${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      showToast("error", "Couldn't download QR");
    }
  };

  return (
    <div
      className={`bg-white rounded-lg p-4 space-y-3 ${
        account ? "border-2 border-primary-300" : "border border-gray-200"
      }`}
      data-testid={account ? "msr-account-cta" : "msr-own-cta"}
    >
      {account ? (
        <div className="space-y-1">
          <div className="font-semibold text-gray-900">Event QR code</div>
          <div className="text-xs text-gray-600">{describeAccountCta(cta)}</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {cta.source_activity_type ? (
              <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-800">
                For: {ctaActivityLabel(cta.source_activity_type)}
              </span>
            ) : null}
            {cta.voucher_amount !== null && cta.voucher_amount !== undefined ? (
              <span className="px-2 py-0.5 rounded bg-green-50 text-green-800">
                ${Number(cta.voucher_amount)} voucher
              </span>
            ) : null}
            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">
              Read-only
            </span>
          </div>
        </div>
      ) : (
        <div className="font-semibold text-gray-900">
          {cta.name ?? "(unnamed CTA)"}
        </div>
      )}
      {cta.qr_code_url !== null && cta.qr_code_url !== undefined ? (
        <img
          src={cta.qr_code_url}
          alt="QR code"
          className="w-full max-w-xs mx-auto rounded border border-gray-100"
        />
      ) : (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {account
            ? "QR not ready yet. The event team (concierge) sets it up; check back before the event."
            : "QR pending generation. Ask your manager to regenerate it."}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleCopyUrl}
          disabled={
            cta.generated_url === null || cta.generated_url === undefined
          }
          className="flex-1 px-3 py-2 rounded border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          Copy URL
        </button>
        <button
          type="button"
          onClick={handleDownloadQr}
          disabled={cta.qr_code_url === null || cta.qr_code_url === undefined}
          className="flex-1 px-3 py-2 rounded bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          Download QR
        </button>
      </div>
    </div>
  );
};

const AttendeeRow: React.FC<{ attendee: MsrEventAttendeeRow }> = ({
  attendee,
}) => (
  <div className="bg-white border border-gray-200 rounded-md p-3 text-sm">
    <div className="flex items-start justify-between gap-2">
      <div className="font-medium text-gray-900">
        {[attendee.first_name, attendee.last_name].filter(Boolean).join(" ") ||
          attendee.email ||
          "(unknown)"}
      </div>
      {attendee.is_zuora_confirmed ? (
        <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-100 text-green-800">
          Verified
        </span>
      ) : (
        <span
          className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800"
          title="Awaiting Zuora subscription confirmation"
        >
          Pending Zuora
        </span>
      )}
    </div>
    {attendee.email !== null && attendee.email !== undefined && (
      <div className="text-xs text-gray-600">{attendee.email}</div>
    )}
    {attendee.phone !== null && attendee.phone !== undefined && (
      <div className="text-xs text-gray-600">{attendee.phone}</div>
    )}
    {attendee.cta_name !== null && attendee.cta_name !== undefined && (
      <div className="mt-1 text-xs text-gray-500">via {attendee.cta_name}</div>
    )}
  </div>
);

/** Event types where members check in, so the rep runs the event from Operations (round 9, D13). */
export const MSR_CHECKIN_EVENT_TYPES = ["range_night", "gun_show", "show"];

export type MsrEventTab = "overview" | "operations" | "attendees" | "ctas";

const TAB_LABELS: Record<MsrEventTab, string> = {
  overview: "Overview",
  operations: "Operations",
  attendees: "Attendees",
  ctas: "Calls to Action",
};

/** The tabs this rep sees for this event. Operations only at a check-in event the rep can operate. */
export function msrEventTabs(
  eventType: string | null | undefined,
  canOperate: boolean,
): MsrEventTab[] {
  const tabs: MsrEventTab[] = ["overview"];
  if (canOperate && MSR_CHECKIN_EVENT_TYPES.includes(eventType ?? "")) {
    tabs.push("operations");
  }
  tabs.push("attendees", "ctas");
  return tabs;
}

/**
 * EVT-06: member verification on the Overview, next to attendance and close rate (which the day cards or the
 * close-rate card above show). Same read and cache key as the Attendees tab's verification section, so a verify there
 * updates this. Works for every event, in or outside the day model. The full list stays on the Attendees tab.
 */
const OverviewVerificationSummary: React.FC<{
  eventId: string;
  onOpenList: () => void;
}> = ({ eventId, onOpenList }) => {
  const summary = useQuery({
    queryKey: ["event-attribution-summary", eventId],
    queryFn: () => getEventAttributionSummary(eventId),
    enabled: !!eventId,
    retry: false,
  });
  // Same query (key + arguments) as the Attendees tab's list, so it is shared and refreshed by a verify there.
  const pool = useQuery({
    queryKey: ["event-signup-pool", eventId, ""],
    queryFn: () => getEventSignupPool(eventId, ""),
    enabled: !!eventId && summary.isSuccess,
    retry: false,
  });
  const c = verificationCounts(summary.data, pool.data);
  return (
    <div
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="msr-overview-verification"
    >
      <h2 className="text-base font-semibold text-gray-900">
        Member verification
      </h2>
      {summary.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {!summary.isLoading && summary.error && (
        <p className="text-sm text-gray-600">
          {/not assigned|permission_denied/i.test(
            (summary.error as { message?: string }).message ?? "",
          )
            ? "You're not on this event's Sales Reps list, so its sign-ups aren't shown."
            : "Verification numbers couldn't be loaded. Reload the page to try again."}
        </p>
      )}
      {!summary.isLoading && !summary.error && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-green-50 px-3 py-2">
              <div className="text-xs text-green-800">Verified</div>
              <div
                className="text-lg font-semibold text-green-900"
                data-testid="msr-overview-verified"
              >
                {c.verified}
              </div>
            </div>
            <div className="rounded-md bg-amber-50 px-3 py-2">
              <div className="text-xs text-amber-800">Waiting to verify</div>
              <div
                className="text-lg font-semibold text-amber-900"
                data-testid="msr-overview-pending"
              >
                {c.pending}
              </div>
            </div>
          </div>
          {c.auto && (
            <p className="text-xs text-gray-500">
              Only one rep is on this event's Sales Reps list, so every sign-up
              is credited to that rep automatically.
            </p>
          )}
        </>
      )}
      <button
        type="button"
        onClick={onOpenList}
        className="w-full min-h-[44px] rounded-md border border-primary-600 bg-white px-4 text-sm font-semibold text-primary-700 hover:bg-primary-50"
      >
        Open the verification list
      </button>
    </div>
  );
};

const RosterRow: React.FC<{ row: OperationsRosterRow; mine?: boolean }> = ({
  row,
  mine = false,
}) => {
  const checkedIn =
    row.attendance_status === "attended" || row.checked_in_at !== null;
  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-gray-900">
          {[row.first_name, row.last_name].filter(Boolean).join(" ") ||
            row.email ||
            "(unknown)"}
        </div>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded ${
            checkedIn
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {checkedIn ? "Checked in" : "Registered"}
        </span>
      </div>
      {row.email !== null && (
        <div className="text-xs text-gray-600">{row.email}</div>
      )}
      {/* Member = the contact's live membership flag (contacts.has_active_subscription, returned as
          is_member_reward_eligible), not attendee_type -- that is how they registered, which goes stale and showed
          "prospect" rows as "Guest". */}
      <div className="mt-1 flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
        <span data-testid="msr-roster-membership">
          {row.is_member_reward_eligible ? "Member" : "Not a member"}
        </span>
        {row.invited_by_name ? (
          <span>· invited by {row.invited_by_name}</span>
        ) : null}
        {mine && (
          <span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">
            Your QR sign-up
          </span>
        )}
      </div>
    </div>
  );
};

const MsrEventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = id ?? "";
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showWalkin, setShowWalkin] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Can THIS rep operate rewards for this event (assigned MSR / granted)? Same event-scoped predicate
  // the operator RPCs enforce — drives whether the Operations surface shows (fail-closed; server also enforces).
  const { data: canOperate } = useQuery({
    queryKey: ["can-operate-event", eventId, user?.id],
    queryFn: async () => {
      if (!eventId || !user?.id) {
        return false;
      }
      const { data } = await supabase.rpc("can_operate_event_reward", {
        p_event_id: eventId,
        p_actor_id: user.id,
      });
      return data === true;
    },
    enabled: !!eventId && !!user?.id,
    // Actively re-resolve the grant (focus + cadence) so a mid-session revocation is caught even on a
    // continuously-open page; server also enforces every action (audit re-iter finding 2).
    refetchOnWindowFocus: true,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // When authorization drops (revoked mid-session), close any operator UI still on screen — the
  // Operations section already hides on !canOperate, but an open walk-in modal must be dismissed too.
  useEffect(() => {
    if (canOperate === false) {
      setShowWalkin(false);
    }
  }, [canOperate]);

  // Share the canonical event-detail cache key with the standard detail page so that
  // EventCloseRateCard's post-save invalidate (eventKeys.detail) refetches THIS page's event too —
  // otherwise the close-rate/status derived from the event prop go stale after an MSR enters attendance.
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: () => getEventById(eventId),
    enabled: !!eventId,
  });

  // MSR slice 4: an event in the date model is started and closed per day (Overview cards below). For any other
  // event the server answers model_active=false and the Overview keeps the existing close-rate card.
  const { data: dayRun } = useEventDayRunState(eventId);
  const dayModel = dayRun?.model_active === true;

  const { data: ctas } = useQuery({
    queryKey: ["msr-event-ctas", eventId],
    queryFn: () => getMsrEventCtas(eventId),
    enabled: !!eventId,
  });

  const { data: attendees, isLoading: attendeesLoading } = useQuery({
    queryKey: ["msr-event-attendees", eventId],
    queryFn: () => getMsrEventAttendees(eventId),
    enabled: !!eventId,
  });

  // The rep's own QR sign-ups, tagged inside the one consolidated roster.
  const mineIds = React.useMemo(
    () => new Set((attendees ?? []).map((a) => a.attendee_id)),
    [attendees],
  );

  // The full roster (read-only here). Same cache key as the Operations tab, so a check-in made there shows
  // here too. Server-gated by can_operate_event_reward; a rep who cannot operate the event sees "Mine" only.
  const { data: roster, isLoading: rosterLoading } = useQuery({
    queryKey: ["event-operations-roster", eventId],
    queryFn: () => getEventOperationsRoster(eventId),
    enabled: !!eventId && canOperate === true,
  });

  // Same-day switcher: the rep's OTHER assigned events on this event's day, so
  // they can hop between events without going back to the full schedule. Include
  // past so it still works the day after. Reuses the schedule cache key.
  const { data: schedule } = useQuery({
    queryKey: ["msr-schedule", true],
    queryFn: () => getMsrSchedule(true, 90),
  });

  const currentDay = event
    ? eventLocalDate({
        start_time: event.start_time,
        start_date: event.start_date,
        timezone: event.timezone,
      })
    : "";

  const sameDay = React.useMemo<
    {
      event_id: string;
      title: string;
      start_time: string | null;
      timezone: string | null;
    }[]
  >(() => {
    if (!currentDay) {
      return [];
    }
    const map = new Map<
      string,
      {
        event_id: string;
        title: string;
        start_time: string | null;
        timezone: string | null;
      }
    >();
    for (const r of schedule ?? []) {
      if (eventLocalDate(r) === currentDay) {
        map.set(r.event_id, {
          event_id: r.event_id,
          title: r.title ?? "Event",
          start_time: r.start_time,
          timezone: r.timezone ?? null,
        });
      }
    }
    // Ensure the current event is present even if the schedule cache lags.
    if (event && !map.has(eventId)) {
      map.set(eventId, {
        event_id: eventId,
        title: event.title ?? "Event",
        start_time: event.start_time ?? null,
        timezone: event.timezone ?? null,
      });
    }
    return [...map.values()].sort((a, b) =>
      (a.start_time ?? "").localeCompare(b.start_time ?? ""),
    );
  }, [schedule, currentDay, event, eventId]);

  // Hours each day of a multi-day event (migration 2026-10-27-1120): so coverage is planned on the real hours.
  const { data: dayHoursMap, isSuccess: dayHoursLoaded } = useEventDayHours(
    event ? [event] : [],
  );
  // undefined until the read succeeds, so "hours not set" is never claimed while loading or after a failed read.
  const dayHoursRows =
    event && dayHoursLoaded ? (dayHoursMap?.get(event.id) ?? []) : undefined;
  const dailyHours = event ? resolveDailyHours(event, dayHoursRows) : null;

  if (eventLoading) {
    return <div className="p-4 text-center text-gray-500">Loading…</div>;
  }

  if (!event) {
    return (
      <div className="p-4 text-center text-gray-600">
        <p>Event not found, or you don't have access to it.</p>
        <Link
          to="/msr"
          className="text-primary-600 underline mt-2 inline-block"
        >
          Back to schedule
        </Link>
      </div>
    );
  }

  // location_address can be an object or JSON text; never render it raw.
  const locationText = formatEventLocation(
    event.location_name,
    event.location_address,
  );
  const mapsLink = locationText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}`
    : null;

  const tabs = msrEventTabs(event.type, canOperate === true);
  const requested = searchParams.get("tab") as MsrEventTab | null;
  const tab: MsrEventTab =
    requested !== null && tabs.includes(requested) ? requested : "overview";
  const selectTab = (t: MsrEventTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <Link
        to="/msr"
        className="text-sm text-primary-600 hover:text-primary-800 inline-block"
      >
        ← My schedule
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-gray-900">
          {event.title ?? "Untitled event"}
        </h1>
        <p className="text-sm text-gray-700">
          {/* In the EVENT's timezone (not the phone's), with the whole span of a multi-day event. With hours set
              for each day, the span shows dates only and the hours are listed per day below. */}
          {dailyHours
            ? formatEventDateRange(
                event.start_time ?? null,
                event.end_time ?? null,
                event.timezone ?? undefined,
              )
            : formatEventSpan(
                event.start_time ?? null,
                event.end_time ?? null,
                event.timezone ?? null,
                event.date_tbd === true,
              )}
        </p>
        {dailyHours ? (
          <EventDailyHoursList days={dailyHours} className="space-y-0.5" />
        ) : dayHoursRows !== undefined ? (
          <EventDailyHoursNotice
            event={event}
            rows={dayHoursRows}
            editHint="Check with the event owner before planning coverage."
          />
        ) : null}
        {locationText && (
          <div className="text-sm text-gray-700">
            <div>{locationText}</div>
            {mapsLink !== null && (
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary-600 hover:text-primary-800 underline mt-1 inline-block"
              >
                Open in Maps
              </a>
            )}
          </div>
        )}
      </header>

      {/* Same-day switcher: hop between the rep's assigned events on this day. */}
      {sameDay.length > 1 && (
        <nav aria-label="Your events this day" className="space-y-2">
          <p className="text-xs font-medium text-gray-500">
            Your events on {dayLabel(currentDay)}
          </p>
          <div className="flex flex-wrap gap-2">
            {sameDay.map((s) => {
              const time = formatTimeInZone(s.start_time, s.timezone);
              const label = time ? `${time} · ${s.title}` : s.title;
              return s.event_id === eventId ? (
                <span
                  key={s.event_id}
                  aria-current="page"
                  className="max-w-[15rem] truncate rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white"
                >
                  {label}
                </span>
              ) : (
                <Link
                  key={s.event_id}
                  to={`/msr/events/${s.event_id}`}
                  className="max-w-[15rem] truncate rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-primary-400 hover:bg-gray-50"
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      <nav
        aria-label="Event sections"
        className="-mx-4 overflow-x-auto border-b border-gray-200 px-4"
      >
        <div role="tablist" className="flex min-w-max gap-1">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              onClick={() => selectTab(t)}
              className={`min-h-[44px] whitespace-nowrap border-b-2 px-3 text-sm font-medium ${
                t === tab
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </nav>

      {tab === "overview" && (
        <section className="space-y-3" aria-label="Overview">
          {/* Date-model events: per-day Start and Close (attendance and close rate are entered/derived at close).
              Other events: attendance entry (class / seminar / speaking engagement) and close rate. The card gates
              its input on the rep's edit right for this event and is read-only otherwise. */}
          {dayModel ? (
            <MsrEventDayRunPanel eventId={eventId} />
          ) : (
            <EventCloseRateCard event={event} />
          )}
          {/* Business lead 2026-10-27: typed-in reinstatements/upgrades next to attendance, and verify a sale by email
              inside the activity (counts toward its close rate). */}
          <ManualCountsCard eventId={eventId} />
          <ActivityVerifySaleCard eventId={eventId} />
          <OverviewVerificationSummary
            eventId={eventId}
            onOpenList={() => {
              selectTab("attendees");
              // after the Attendees tab renders, bring its verification section into view
              setTimeout(() => {
                document
                  .getElementById("msr-verify-signups")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
          />
        </section>
      )}

      {/* Operator surface (parity with the admin event page): live roster, reward state, Fix, referral,
          walk-in and scan-admit. Shown only at check-in events this rep can operate; the server enforces
          every action. */}
      {tab === "operations" && canOperate && (
        <section className="space-y-2" aria-label="Operations">
          <EventOperationsTab
            eventId={eventId}
            onWalkIn={() => setShowWalkin(true)}
            canCheckIn={["scheduled", "in_progress"].includes(
              event?.status || "",
            )}
            endTime={event?.end_time ?? null}
          />
        </section>
      )}

      {tab === "attendees" && (
        <section className="space-y-4" aria-label="Attendees">
          {canOperate ? (
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-gray-900">
                Attendees
                {roster !== undefined && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({roster.length})
                  </span>
                )}
              </h2>
              {rosterLoading ? (
                <div className="text-gray-500 text-sm">Loading…</div>
              ) : roster === undefined || roster.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
                  No one is registered for this event yet.
                </div>
              ) : (
                roster.map((r) => (
                  <RosterRow
                    key={r.attendee_id}
                    row={r}
                    mine={mineIds.has(r.attendee_id)}
                  />
                ))
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-gray-900">
                Attendees
                {attendees !== undefined && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({attendees.length})
                  </span>
                )}
              </h2>
              <p className="text-xs text-gray-500">
                People who signed up through your QR codes.
              </p>
              {attendeesLoading ? (
                <div className="text-gray-500 text-sm">Loading…</div>
              ) : attendees === undefined || attendees.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
                  No one's signed up through your QR codes yet.
                </div>
              ) : (
                attendees.map((a) => (
                  <AttendeeRow key={a.attendee_id} attendee={a} />
                ))
              )}
            </div>
          )}

          {/* Member Verification sits below the roster (round 9, D15: not a tab of its own). For an msr the
              shared component verifies as self; the pool and verify RPCs are scoped server-side. */}
          <div
            id="msr-verify-signups"
            className="space-y-3 border-t border-gray-200 pt-4"
          >
            <h2 className="text-lg font-semibold text-gray-900">
              Verify member sign-ups
            </h2>
            <MemberVerificationTab eventId={eventId} />
          </div>
        </section>
      )}

      {tab === "ctas" && eventTypeIsNoPromo(event.type) && (
        <section className="space-y-3" aria-label="Calls to Action">
          {/* Owner 2026-10-29: a class / table / seminar / speaking engagement has no event QR code; the rep uses
              the venue's (read-only, same panel as the CRM event page). */}
          <EventHostCTAsPanel event={event} />
          {ctas !== undefined &&
            splitMsrEventCtas(ctas).mine.map((cta) => (
              <CtaCard key={cta.cta_id} cta={cta} />
            ))}
        </section>
      )}

      {tab === "ctas" && !eventTypeIsNoPromo(event.type) && (
        <section className="space-y-3" aria-label="Calls to Action">
          {ctas === undefined ? (
            <div className="text-gray-500 text-sm">Loading…</div>
          ) : (
            (() => {
              const { account, mine } = splitMsrEventCtas(ctas);
              return (
                <>
                  {account !== null &&
                    (eventQrNotReady(account) ? (
                      <div
                        className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1"
                        data-testid="msr-account-cta-not-ready"
                      >
                        <div className="font-semibold text-gray-900">
                          Event QR code
                        </div>
                        <div className="text-sm text-amber-900">
                          {EVENT_QR_NOT_READY}
                        </div>
                      </div>
                    ) : (
                      <CtaCard key={account.cta_id} cta={account} account />
                    ))}
                  <h2 className="text-lg font-semibold text-gray-900">
                    My QR codes
                  </h2>
                  {mine.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
                      {account === null
                        ? "You don't have a QR code on this event. If you're working it, ask your manager to assign one to you."
                        : eventQrNotReady(account)
                          ? "You don't have a personal QR code on this event."
                          : "You don't have a personal QR code on this event. Use the event QR code above."}
                    </div>
                  ) : (
                    mine.map((cta) => <CtaCard key={cta.cta_id} cta={cta} />)
                  )}
                </>
              );
            })()
          )}
        </section>
      )}

      {showWalkin && event && (
        <EventWalkinPanel
          eventId={eventId}
          eventTitle={event.title ?? "Event"}
          onClose={() => setShowWalkin(false)}
          onRegistered={() => {
            void queryClient.invalidateQueries({
              queryKey: ["event-operations-roster", eventId],
            });
            void queryClient.invalidateQueries({
              queryKey: ["msr-event-attendees", eventId],
            });
          }}
        />
      )}
    </div>
  );
};

export default MsrEventDetail;
